<template>
  <CenterContent v-else size="xs12">
    <SP :class="['mb-3', 'text-subtitle-1', 'font-weight-light']">{{ viewHelpers.description }}</SP>
    <v-card class="pa-2" outlined>
      <TextBox
          :value="accounts"
          :label="viewHelpers.textBoxLabel"
          :hint="viewHelpers.textBoxHint"
          :error="textBoxError"
          :disabled="alert.loading"
          @input="onAccountChange" />
      <Alert loading :type="alert.variant" :message="alert.message" />
      <Alert outlined type="error" border="left" :message="warningError" />
      <Alert outlined :type="response.variant" border="left" :message="response.message" />
      <v-row>
        <v-col cols="6">
          <Button
              title="Scrape Credit(s)"
              :disabled="alert.loading"
              :class-object="['px-10']"
              @onClick="onButtonClick"
              prepend-icon="bx bx-refresh"
              outlined />
        </v-col>
        <v-col cols="6">
          <Button
              title="Show My History"
              :disabled="alert.loading"
              :class-object="['px-10', 'float-right']"
              @onClick="onHistoryClick"
              prepend-icon="bx bx-history"
              outlined />
        </v-col>
      </v-row>
      <TicketList
        v-if="ticketItems != null"
        title="Credits"
        :searchable="false"
        :selectable="false"
        :filterable="false"
        :items="ticketItems"
        :class-div-object="['my-10']" />
    </v-card>
    <BottomSheet
        v-model="historySheet"
        class="mb-10"
        @onClose="onHistoryClose">
      <Alert type="error" :message="history.message" />
      <DataTable
          title="History"
          custom-loading-type="table-thead, table-tbody, table-tfoot"
          filterable
          group-by="status"
          :custom-actions="customActions"
          :loading="history.loading"
          :custom-options="datatableOptions"
          :custom-header-values="historyHeaderValues"
          :datatable-items="historyItems">

        <template v-slot:item.accounts="{ item }">
          <TooltipLabel v-if="item.accounts_tooltip && item.accounts_tooltip !== ''" top :label="item.accounts_tooltip">
            {{ item.accounts }}
          </TooltipLabel>
          <span v-else>{{ item.accounts }}</span>
        </template>

        <template v-slot:item.result="{ item }">
          <TooltipLabel v-if="item.result_tooltip && item.result_tooltip !== ''" top :label="item.result_tooltip">
            {{ item.result }}
          </TooltipLabel>
          <span v-else>{{ item.result }}</span>
        </template>
      </DataTable>
    </BottomSheet>
  </CenterContent>
</template>

<script>
import SP from "@/base_lib/components/container/SP.vue"
import CenterContent from "@/base_lib/components/container/CenterContent.vue"
import TextBox from "@/base_lib/components/form/TextBox.vue"
import Button from "@/base_lib/components/form/Button.vue"
import TicketList from "@/view/other/TicketList.vue"
import {baseValidation} from "@/data/view/validate"
import helper from "@/utils/viewHelper"
import PageMixin from "@/base_lib/data/mixins/pageMixin"
import ScrapeService from "@/services/ScrapeService";
import accountModalStructure from "@/data/view/accountStructure"
import RequestService from "@/services/RequestService"
import Alert from "@/base_lib/components/view/Alert.vue"
import BottomSheet from "@/base_lib/components/container/BottomSheet.vue"
import DataTable from "@/base_lib/components/container/DataTable.vue"
import TooltipLabel from "@/base_lib/components/container/TooltipLabel.vue"
import socketMixin from "@/data/mixins/socketMixin"
import pageProjectMixin from "@/data/mixins/pageProjectMixin"

export default {
  name: "credits",
  components: {
    TooltipLabel, DataTable, BottomSheet, Alert,
    TicketList, Button, TextBox, CenterContent, SP
  },
  mixins: [PageMixin, socketMixin, pageProjectMixin],
  computed: {
    customActions() {
      return [
        {
          title: "View",
          click: this.onHistoryItemClick,
          isRendered: (item) => {
            return item.status === 'completed' && !item.result?.includes("any ticket")
          }
        },
        {
          title: "Retry",
          click: this.onHistoryItemRetryClick,
          isRendered: (item) => {
            return item.result?.includes("any credit")
          }
        },
      ]
    },
  },
  data: () => ({
    accounts: null,

    // TextBox variables
    warningError: null,
    textBoxError: "",
    textBoxErrorLines: [],

    // Loading
    alert: {
      message: null,
      variant: "info",
      loading: false,
    },

    // History variables
    historySheet: false,
    historyItems: [],
    historyHeaderValues: [],
    historyErrorMessage: null,
    datatableOptions: {
      page: 1,
      itemsPerPage: 5,
      itemsPerPageOptions: [5],
    },
    creditOptions: {
      page: 1,
      itemsPerPage: 20,
      itemsPerPageOptions: [20, 50],
    },

    // Ticket items
    ticketItems: null,
    successMessage: null,
  }),
  methods: {
    onButtonClick() {
      this.resetHistoryId()
      this.warningError = null
      this.response.message = null
      this.textBoxError = ""
      this.textBoxErrorLines = []
      this.ticketItems = null
      this.successMessage = null

      let validateResponse = baseValidation(this.accounts, this.viewHelpers.rules)
      if (validateResponse.success === false) {
        this.textBoxError = validateResponse.message
        this.warningError = validateResponse.other.map(line => line.message).join("<br/>")
        return
      }

      this.warningError = validateResponse.other.map(line => line.message).join("<br/>")

      let accounts = accountModalStructure(this.$route.meta.type, validateResponse.validLines)
      let data = {
        accounts: accounts,
        custom_data: {
          ...this.custom_data,
          type: "CREDITS",
        },
      }
      this.alert.loading = true
      this.alert.message = "Please stand by"
      this.response.loading = true
      this.scrollToTop()

      ScrapeService.credits(data).then(response => {
        if (response.data.data.request_status === "requested") {
          this.socketRequestId = response.data.data.request_id
          this.alert.message = "We got your request. Scraping credits... \nYou can close this page and check it later from history. (" + response.data.data.request_id + ")"
        } else {
          this.warningError = "An error occurred. Please try again"
          this.alert.message = null
          this.alert.loading = false
        }
        this.scrollToTop()
      }).catch(error => {
        if (error.networkError === 'function') {
          this.response = error.networkError?.(error)
        } else {
          this.response = this.returnCustomError(error.toString())
        }
        this.alert.message = null
        this.alert.loading = false
      }).finally(() => {
        this.response.loading = false
      })
    },
    onAccountChange(accounts) {
      this.accounts = accounts
    },
    onHistoryClick() {
      this.history.loading = true
      this.historySheet = true

      RequestService.history(this.viewHelpers.filter).then(response => {
        this.history = response.networkResponse(true)
        if (response.data.data.list.length > 0) {
          this.historyItems = this.decorateHistoryData(response.data.data.list)
          this.historyHeaderValues = Object.keys(this.historyItems[0]).filter(item => this.historyHeaderIgnore.includes(item) === false)
        }
      }).catch(error => {
        if (error.networkError === 'function') {
          this.history = error.networkError?.(error)
        } else {
          this.history = this.returnCustomError(error.toString())
        }
      }).finally(() => {
        this.response.loading = false
      })
    },
    decorateHistoryData(items) {
      return items.map(item => ({
        request_id: item.request_uuid,
        editor: [item.editor.first_name, item.editor.last_name].join(" "),
        editor_object: item.editor,

        accounts: `${item.request_data.accounts.length} accounts`,
        accounts_tooltip: item.request_data.accounts.map(account => `${account.account_team}:${account.account_user}`).join("<br/>"),

        result: item.api_response_message,
        result_tooltip: item.error_summary?.join("<br />"),

        status: item.request_status,
        request_time: this.printableDate(item.created_at),

        action: "",
        _search: JSON.stringify(item).toLowerCase(),
        _item: item,
      }))
    },
    getTicketList(id) {
      RequestService.historyDetail(id).then(response => {
        if (this.alert.loading === false) {
          return
        }
        let data = response.data.data.decorated_response

        this.accounts = response.data.data?.request_data?.accounts?.map(a => [a.account_team, a.account_user, a.account_password]).join("\n") || ""

        if (Array.isArray(data) && data.length > 0) {
          this.ticketItems = data
        } else if (response.data.code === 100) {
          this.ticketItems = null
          this.response.variant = 'error'
          this.response.message = this.getDetailedError(response)
        } else {
          this.response.variant = 'error'
          this.response.message = "An error occurred. Please try again."
        }

        this.alert.message = null
        this.alert.loading = false
        this.scrollToTop()
      }).catch(error => {
        if (error.networkError === 'function') {
          this.response = error.networkError?.(error)
        } else {
          this.response = this.returnCustomError(error.toString())
        }
      }).finally(() => {
        this.alert.message = null
        this.alert.loading = false
      })
    },
    onHistoryItemClick(item) {
      this.response.message = null
      let id = typeof item === 'object' ? item.request_id : item
      this.getTicketList(id)
      this.accounts = ""
      this.alert.loading = true
      this.alert.message = "Please stand by"
      this.onHistoryClose()
    },
    onHistoryClose() {
      this.historySheet = false
      this.historyItems = []
    },
    onSocketData(data) {
      this.getTicketList(data.request_id)
    },
    checkParams() {
      if (this.$route.query.accounts) {
        this.accounts = this.$route.query.accounts
      }
    }
  },
  mounted() {
    this.checkParams()
  },
  created() {
    this.hasSocket = true
    this.viewHelpers = helper(this.$route.meta.type)
    this.accounts = this.viewHelpers.textBoxModel
  },
}
</script>